import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import type { VariantProps } from "class-variance-authority";

import { buttonVariants } from "@beweegbaas/ui/components/ui/button";
import { focusStyles } from "@beweegbaas/ui/lib/focus-styles";
import { cn } from "@beweegbaas/ui/lib/utils";

type ButtonLook = VariantProps<typeof buttonVariants>;

export interface ToggleLook {
  /** The `Button` variant while it is off; once on, it is a filled primary button. */
  variant?: ButtonLook["variant"];
  size?: ButtonLook["size"];
}

/** A toggle looks like a `Button`: of its own variant while off, the primary one while on. */
function toggleClassName(
  { variant = "outline", size = "default" }: ToggleLook,
  className: TogglePrimitive.Props["className"],
) {
  return (state: TogglePrimitive.State) =>
    cn(
      buttonVariants({ variant: state.pressed ? "default" : variant, size }),
      // A Button spaces its content with gap-2 in a wrapper of its own, which a toggle has not.
      "gap-2",
      focusStyles,
      typeof className === "function" ? className(state) : className,
    );
}

/** A button that stays pressed until it is pressed again, such as a filter that is on or off. */
function Toggle<Value extends string>({
  variant,
  size,
  className,
  ...props
}: TogglePrimitive.Props<Value> & ToggleLook) {
  return (
    <TogglePrimitive
      data-slot="toggle"
      className={toggleClassName({ variant, size }, className)}
      {...props}
    />
  );
}

export { Toggle, toggleClassName };
